import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, openModal } from "risa-oasis-ui_v2";
import DocViewerWithSideNavigation from "../../components/modals/docViewerWithSideNavigation/docViewerWithSideNavigation";
import PdfRender from "../../components/pdfRender/pdfRender";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../enums/eventsData";
import { ModalId } from "../../enums/modalId";
import {
  setCurrentDocumentIndex,
  updateAnswer,
} from "../../redux/slice/medicalNecessityReviewSlice";
import { closeModal, setOpenedModalId } from "../../redux/slice/modalSlice";
import { RootState } from "../../redux/store/store";
import ManageAttachmentsModal from "./components/manageAttachmentsModal";
import ReviewChecklist from "./components/reviewChecklist";
import SimpleDocViewerModal from "./components/simpleDocViewerModal";
import { useMedicalNecessityData } from "./hooks/useMedicalNecessityData";
import {
  determineGuidelineType,
  getGuidelineButtonText,
} from "./utils/guidelineSelector";
import { getLetterTitle, getLetterUrl } from "./utils/letterSelector";
import { saveAnswers } from "./utils/saveAnswers";

const MedicalNecessityReview: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch data using custom hook
  const { checklistData, emrDocumentUrl, guidelineItems, loading, error } =
    useMedicalNecessityData(orderId || "");

  // Get user answers and patient data from Redux
  const userAnswers = useSelector(
    (state: RootState) => state.medicalNecessityReview.userAnswers,
  );
  const emrDocumentUrls = useSelector(
    (state: RootState) => state.medicalNecessityReview.emrDocumentUrls,
  );
  const currentDocumentIndex = useSelector(
    (state: RootState) => state.medicalNecessityReview.currentDocumentIndex,
  );
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const saving = useSelector(
    (state: RootState) => state.medicalNecessityReview.saving,
  );
  const { openedModalId } = useSelector((state: RootState) => state.modal);

  // Open modal when Redux state changes - required for risa-oasis-ui_v2 Modal component
  useEffect(() => {
    if (openedModalId === ModalId.DOC_VIEWER_MODAL_WITH_TITLE) {
      openModal(ModalId.DOC_VIEWER_MODAL_WITH_TITLE);
    } else if (openedModalId === ModalId.MANAGE_ATTACHMENTS_MODAL) {
      openModal(ModalId.MANAGE_ATTACHMENTS_MODAL);
    } else if (openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION) {
      openModal(ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION);
    }
  }, [openedModalId]);

  // Debug modal state
  console.log("📊 Current openedModalId:", openedModalId);
  console.log(
    "📊 DOC_VIEWER_MODAL_WITH_TITLE value:",
    ModalId.DOC_VIEWER_MODAL_WITH_TITLE,
  );

  // Determine guideline type based on insurance
  const guidelineType = useMemo(() => {
    const insuranceType =
      singleMedicalPaOrder?.coverage?.primary?.payer_name || "";
    return determineGuidelineType(insuranceType);
  }, [singleMedicalPaOrder]);

  // Get patient name
  const patientName = useMemo(() => {
    if (!singleMedicalPaOrder) return "Patient";
    const firstName = singleMedicalPaOrder.demographics?.first_name || "";
    const lastName = singleMedicalPaOrder.demographics?.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Patient";
  }, [singleMedicalPaOrder]);

  // Get current document from gallery
  const currentDocumentUrl =
    emrDocumentUrls[currentDocumentIndex] || emrDocumentUrl;
  const hasMultipleDocuments = emrDocumentUrls.length > 1;

  // Navigation handlers for document gallery
  const handlePreviousDocument = () => {
    if (currentDocumentIndex > 0) {
      dispatch(setCurrentDocumentIndex(currentDocumentIndex - 1));
    }
  };

  const handleNextDocument = () => {
    if (currentDocumentIndex < emrDocumentUrls.length - 1) {
      dispatch(setCurrentDocumentIndex(currentDocumentIndex + 1));
    }
  };

  // Handle answer change
  const handleAnswerChange = useCallback(
    (questionId: string, answer: any) => {
      dispatch(updateAnswer({ questionId, answer }));
    },
    [dispatch],
  );

  // Handle save and navigation
  const handleSave = async () => {
    if (!orderId) return;
    try {
      await saveAnswers(orderId, userAnswers);
    } catch (error) {
      console.error("Failed to save answers:", error);
      // TODO: Show error toast
    }
  };

  const handleGoBack = async () => {
    await handleSave();
    navigate(`/worklists/singleOrder/${orderId}/cptCodes`);
  };

  const handleGoBackToOrders = async () => {
    await handleSave();
    navigate("/worklists/medical-pa-orders");
  };

  const handleViewGuidelines = () => {
    console.log("🔵 View Guidelines clicked");

    // Check if we have guideline items for the side navigation modal
    if (guidelineItems && guidelineItems.length > 0) {
      console.log(
        "🔵 Using guideline items for side navigation modal:",
        guidelineItems,
      );

      // Use the DocViewerWithSideNavigation modal with J-code tabs
      dispatch(
        setOpenedModalId({
          id: ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION,
          metaData: {
            items: guidelineItems,
          },
        }),
      );
    } else {
      console.log("🔵 No guideline items available");
      // Fallback: show a message or do nothing
      console.error("❌ No guideline items available");
    }
  };

  const handleManageAttachments = () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.MANAGE_ATTACHMENTS_MODAL,
      }),
    );
  };

  const handleViewLetter = () => {
    console.log("🟢 View Letter clicked");
    console.log("🟢 Order ID:", orderId);
    if (!orderId) {
      console.error("❌ No order ID available");
      return;
    }

    const letterUrl = getLetterUrl(orderId);
    const letterTitle = getLetterTitle(orderId);
    console.log("🟢 Letter URL:", letterUrl);
    console.log("🟢 Letter Title:", letterTitle);

    console.log("🟢 Dispatching modal open");
    dispatch(
      setOpenedModalId({
        id: ModalId.DOC_VIEWER_MODAL_WITH_TITLE,
        metaData: {
          title: letterTitle,
          docUrl: letterUrl,
        },
      }),
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mb-4">Loading medical necessity review...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded border border-red-200 bg-red-50 p-4">
          <div className="mb-2 font-semibold text-red-600">Error</div>
          <div className="text-red-600">{error}</div>
          <div className="mt-4">
            <Button
              buttonType="primary"
              size="small"
              onClick={() => window.location.reload()}
              disabled={false}
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="medical-necessity-review flex h-full flex-col bg-gray-50">
      {/* Header */}
      <header className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <h1 className="text-h10 font-bold leading-h10 text-primaryGray-1">
              Clinical Decision Support
            </h1>
            <span className="text-h11 font-semiBold leading-h11 text-primaryGray-2">
              {patientName}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleViewGuidelines}
              className="text-small font-bold text-blue-600 transition-colors hover:text-blue-700"
            >
              {getGuidelineButtonText(guidelineType)}
            </button>
            <Button
              buttonType="secondary"
              size="medium"
              onClick={handleManageAttachments}
              disabled={false}
            >
              Manage Attachments
            </Button>
            <Button
              buttonType="primary"
              size="medium"
              onClick={handleViewLetter}
              disabled={false}
            >
              View Letter
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - Split Pane */}
      <div className="content flex flex-1 overflow-hidden">
        {/* Left Pane - PDF Viewer */}
        <div className="pdf-pane w-[50%] overflow-auto border-r bg-white p-6">
          {/* Gallery Navigation */}
          {hasMultipleDocuments && (
            <div className="mb-4 flex items-center justify-between">
              <Button
                buttonType="secondary"
                size="small"
                onClick={handlePreviousDocument}
                disabled={currentDocumentIndex === 0}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                {currentDocumentIndex + 1} / {emrDocumentUrls.length}
              </span>
              <Button
                buttonType="secondary"
                size="small"
                onClick={handleNextDocument}
                disabled={currentDocumentIndex === emrDocumentUrls.length - 1}
              >
                Next
              </Button>
            </div>
          )}

          {/* Document Viewer */}
          {currentDocumentUrl ? (
            <PdfRender file={currentDocumentUrl} />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-500">
              <div className="text-center">
                <div className="mb-2">No clinical attachments available</div>
                <div className="text-sm">
                  Folder path: onco_emr/{orderId}/clinical_attachment
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Pane - Review Checklist */}
        <div className="checklist-pane w-[50%] overflow-auto bg-white p-6">
          {checklistData ? (
            <ReviewChecklist
              questions={checklistData.questions}
              currentAnswers={userAnswers}
              onAnswerChange={handleAnswerChange}
              orderId={orderId || ""}
              recommendation={checklistData.metadata?.recommendation}
              recommendationText={checklistData.metadata?.recommendation_text}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-500">
              No checklist data available
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="flex justify-end gap-3 border-t bg-white px-6 py-4">
        <Button
          buttonType="secondary"
          size="medium"
          onClick={handleGoBack}
          disabled={saving}
        >
          Go Back
        </Button>
        <button
          onClick={handleGoBackToOrders}
          disabled={saving}
          className="rounded-md bg-primaryGray-2 px-6 py-2.5 text-small font-semiBold text-white transition-colors hover:bg-primaryGray-1 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "Saving..." : "Go Back to Orders"}
        </button>
      </footer>

      {/* Manage Attachments Modal */}
      {openedModalId === ModalId.MANAGE_ATTACHMENTS_MODAL && (
        <ManageAttachmentsModal
          onClose={() => dispatch(closeModal())}
          orderId={orderId || ""}
        />
      )}

      {/* Document Viewer Modal (for Letters) */}
      {openedModalId === ModalId.DOC_VIEWER_MODAL_WITH_TITLE && (
        <SimpleDocViewerModal onClose={() => dispatch(closeModal())} />
      )}

      {/* Guidelines Modal with Side Navigation (J-code tabs) */}
      {openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION && (
        <DocViewerWithSideNavigation
          screenName={ScreenNamesForBigQuery.MEDICAL_NECESSITY_REVIEW_SCREEN}
          shouldLogEvent={true}
          modalName={ModalNamesForBigQuery.NCCN_GUIDELINES_MODAL}
        />
      )}
    </div>
  );
};

export default MedicalNecessityReview;
